/**
 * Team Performance — the Analytics view.
 *
 * Reads the SAME aggregated rows the table renders (the report is fetched
 * whole, bounded by the filter drawer), so switching tabs never re-queries
 * and the two views can never disagree. Every cross-team panel — leaderboard,
 * SLA attainment, load vs attainment, workload/outcome mix, transfers — is
 * built from those rows alone. Mirrors AgentPerformanceAnalytics re-keyed
 * from agent to team; see that file for the fuller rationale.
 *
 * The aggregated rows carry no timestamps, so the two panels that need
 * individual conversation times — volume by weekday×hour, and the response/
 * resolution duration distribution — read `timelineRows` instead: a separate,
 * lazily-fetched set of raw per-conversation rows scoped identically to the
 * aggregate query (see useTeamPerformanceTimeline). They render their own
 * loading state and degrade to an empty-state message if the fetch hasn't
 * completed yet, rather than blocking the rest of the view on it.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  Activity, AlertTriangle, ArrowLeftRight, CalendarClock, Clock, Gauge, Layers, ListOrdered,
  Loader2, Timer, TrendingUp, UsersRound,
} from 'lucide-react'

import type { TeamPerformanceRow, TeamTimelineRow } from '../../../hooks/useTeamPerformanceReport.js'
import {
  AnalyticsCanvas,
  AttentionList,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  Heatmap,
  HeroBand,
  KpiRow,
  Meter,
  RankTable,
  SectionLabel,
  StatTile,
  type AttentionItem,
  type BarListItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  barDataset,
  barOptions,
  durationBuckets,
  formatCount,
  formatPct,
  formatSeconds,
  hourDayMatrix,
  medianOf,
  ratePct,
  scatterDataset,
  scatterOptions,
  sumBy,
  weightedMean,
} from '../analytics/viz.js'

/** One team's totals — rows are already per-team, but duplicates are merged defensively. */
interface TeamAgg {
  key: string
  name: string
  assigned: number
  closed: number
  workInProgress: number
  reOpened: number
  transferToTeam: number
  transferByTeam: number
  notes: number
  responses: number
  avgResponseSeconds: number | null
  avgResolutionSeconds: number | null
  responseSlaPct: number | null
  resolutionSlaPct: number | null
}

/** How many teams a per-team chart shows before the tail is dropped. */
const CHART_TEAMS = 8
/** Below this, a team is called out in the attention panel. */
const SLA_TARGET = 90
/** Re-open share that reads as a quality problem rather than noise. */
const REOPEN_TARGET = 10

function aggregate(rows: readonly TeamPerformanceRow[]): TeamAgg[] {
  const byTeam = new Map<string, TeamPerformanceRow[]>()
  for (const row of rows) {
    const key = row.teamId || row.teamName
    const bucket = byTeam.get(key)
    if (bucket) bucket.push(row)
    else byTeam.set(key, [row])
  }

  const out: TeamAgg[] = []
  for (const [key, group] of byTeam) {
    const first = group[0]
    if (!first) continue
    out.push({
      key,
      name: first.teamName,
      assigned: sumBy(group, (r) => r.assigned),
      closed: sumBy(group, (r) => r.closed),
      workInProgress: sumBy(group, (r) => r.workInProgress),
      reOpened: sumBy(group, (r) => r.reOpened),
      transferToTeam: sumBy(group, (r) => r.transferToTeam),
      transferByTeam: sumBy(group, (r) => r.transferByTeam),
      notes: sumBy(group, (r) => r.notes),
      responses: sumBy(group, (r) => r.responses),
      // Averages are weighted by the volume each one summarises, never by row count.
      avgResponseSeconds: weightedMean(group.map((r) => [r.avgResponseSeconds, r.responses])),
      avgResolutionSeconds: weightedMean(group.map((r) => [r.avgResolutionSeconds, r.closed])),
      responseSlaPct: weightedMean(group.map((r) => [r.responseSlaPct, r.assigned])),
      resolutionSlaPct: weightedMean(group.map((r) => [r.resolutionSlaPct, r.closed])),
    })
  }
  return out
}

export default function TeamPerformanceAnalytics({
  rows,
  timelineRows,
  timelineLoading,
  timelineTruncated,
}: {
  rows: readonly TeamPerformanceRow[]
  /** Raw per-conversation rows for the heatmap and duration charts; see file header. */
  timelineRows: readonly TeamTimelineRow[]
  timelineLoading: boolean
  timelineTruncated: boolean
}): JSX.Element {
  const teams = useMemo(() => aggregate(rows), [rows])

  const totals = useMemo(() => {
    const assigned = sumBy(teams, (t) => t.assigned)
    const closed = sumBy(teams, (t) => t.closed)
    return {
      assigned,
      closed,
      workInProgress: sumBy(teams, (t) => t.workInProgress),
      reOpened: sumBy(teams, (t) => t.reOpened),
      responses: sumBy(teams, (t) => t.responses),
      notes: sumBy(teams, (t) => t.notes),
      transfersIn: sumBy(teams, (t) => t.transferToTeam),
      transfersOut: sumBy(teams, (t) => t.transferByTeam),
      closureRate: ratePct(closed, assigned),
      // Org-wide averages weight each team by the volume behind their number.
      avgResponse: weightedMean(teams.map((t) => [t.avgResponseSeconds, t.responses])),
      avgResolution: weightedMean(teams.map((t) => [t.avgResolutionSeconds, t.closed])),
      responseSla: weightedMean(teams.map((t) => [t.responseSlaPct, t.assigned])),
      resolutionSla: weightedMean(teams.map((t) => [t.resolutionSlaPct, t.closed])),
      activeTeams: teams.filter((t) => t.assigned > 0 || t.responses > 0).length,
    }
  }, [teams])

  const byVolume = useMemo(
    () => [...teams].sort((a, b) => b.assigned - a.assigned || b.closed - a.closed),
    [teams],
  )
  const maxAssigned = byVolume[0]?.assigned ?? 0

  const topForCharts = useMemo(
    () => byVolume.filter((t) => t.assigned > 0 || t.closed > 0).slice(0, CHART_TEAMS),
    [byVolume],
  )

  /** Volume spread across the roster — the sparkline behind the hero figure. */
  const volumeShape = useMemo(() => byVolume.map((t) => t.assigned).slice(0, 24), [byVolume])

  // ── Assigned vs closed ─────────────────────────────────────────────────────
  const handledData = useMemo(
    () => ({
      labels: topForCharts.map((t) => t.name),
      datasets: [
        barDataset('Assigned', topForCharts.map((t) => t.assigned), VIZ.series[0]),
        barDataset('Closed', topForCharts.map((t) => t.closed), VIZ.series[2]),
      ],
    }),
    [topForCharts],
  )

  // ── SLA attainment ─────────────────────────────────────────────────────────
  const slaTeams = useMemo(
    () =>
      byVolume
        .filter((t) => t.responseSlaPct !== null || t.resolutionSlaPct !== null)
        .slice(0, CHART_TEAMS),
    [byVolume],
  )
  const slaData = useMemo(
    () => ({
      labels: slaTeams.map((t) => t.name),
      datasets: [
        barDataset('Response SLA', slaTeams.map((t) => t.responseSlaPct ?? 0), VIZ.series[0]),
        barDataset('Resolution SLA', slaTeams.map((t) => t.resolutionSlaPct ?? 0), VIZ.series[1]),
      ],
    }),
    [slaTeams],
  )

  // ── Handling time ──────────────────────────────────────────────────────────
  const timeTeams = useMemo(
    () =>
      byVolume
        .filter((t) => t.avgResponseSeconds !== null || t.avgResolutionSeconds !== null)
        .slice(0, CHART_TEAMS),
    [byVolume],
  )
  const timeData = useMemo(
    () => ({
      labels: timeTeams.map((t) => t.name),
      datasets: [
        barDataset('First response', timeTeams.map((t) => t.avgResponseSeconds ?? 0), VIZ.series[0]),
        barDataset('Resolution', timeTeams.map((t) => t.avgResolutionSeconds ?? 0), VIZ.series[1]),
      ],
    }),
    [timeTeams],
  )

  // ── Workload mix ───────────────────────────────────────────────────────────
  const mixData = useMemo(
    () => ({
      labels: topForCharts.map((t) => t.name),
      datasets: [
        barDataset('Closed', topForCharts.map((t) => t.closed), VIZ.series[2], { stacked: true }),
        barDataset('In progress', topForCharts.map((t) => t.workInProgress), VIZ.series[0], { stacked: true }),
        barDataset('Re-opened', topForCharts.map((t) => t.reOpened), VIZ.series[1], { stacked: true }),
      ],
    }),
    [topForCharts],
  )

  // ── Load vs attainment ─────────────────────────────────────────────────────
  //
  // The question a table cannot answer at a glance: does service level hold as
  // a team's load goes up? A falling cloud left-to-right is the finding.
  const scatterTeams = useMemo(
    () => teams.filter((t) => t.assigned > 0 && t.responseSlaPct !== null),
    [teams],
  )
  const scatterData = useMemo(
    () => ({
      datasets: [
        scatterDataset(
          'Teams',
          scatterTeams.map((t) => ({ x: t.assigned, y: t.responseSlaPct ?? 0 })),
          VIZ.series[0],
        ),
      ],
    }),
    [scatterTeams],
  )

  // ── Volume share ────────────────────────────────────────────────────────────
  //
  // The leaderboard has the numbers; this is the same ranking read as a shape —
  // how concentrated the load is across the roster, at a glance.
  const shareItems = useMemo(
    (): BarListItem[] =>
      byVolume
        .filter((t) => t.assigned > 0)
        .map((t) => ({
          label: t.name,
          value: t.assigned,
          meta: formatPct(ratePct(t.assigned, totals.assigned), 1),
        })),
    [byVolume, totals.assigned],
  )

  // ── Transfers ───────────────────────────────────────────────────────────────
  const transferTeams = useMemo(
    () =>
      byVolume
        .filter((t) => t.transferToTeam > 0 || t.transferByTeam > 0)
        .slice(0, CHART_TEAMS),
    [byVolume],
  )
  const transferData = useMemo(
    () => ({
      labels: transferTeams.map((t) => t.name),
      datasets: [
        barDataset('Transferred in', transferTeams.map((t) => t.transferToTeam), VIZ.series[0]),
        barDataset('Transferred out', transferTeams.map((t) => t.transferByTeam), VIZ.series[1]),
      ],
    }),
    [transferTeams],
  )

  // ── Collaboration ───────────────────────────────────────────────────────────
  const collabTeams = useMemo(
    () => byVolume.filter((t) => t.notes > 0 || t.responses > 0).slice(0, CHART_TEAMS),
    [byVolume],
  )
  const collabData = useMemo(
    () => ({
      labels: collabTeams.map((t) => t.name),
      datasets: [
        barDataset('Responses', collabTeams.map((t) => t.responses), VIZ.series[0]),
        barDataset('Notes', collabTeams.map((t) => t.notes), VIZ.series[2]),
      ],
    }),
    [collabTeams],
  )

  // ── Volume by weekday × hour ────────────────────────────────────────────────
  // Needs individual timestamps — drawn from timelineRows, not the aggregate.
  const openedMatrix = useMemo(
    () => hourDayMatrix(timelineRows as unknown as Record<string, unknown>[], 'openedAt'),
    [timelineRows],
  )

  // ── Handling time distribution ──────────────────────────────────────────────
  const responseSecondsRaw = useMemo(
    () => timelineRows.map((r) => r.responseSeconds),
    [timelineRows],
  )
  const resolutionSecondsRaw = useMemo(
    () => timelineRows.map((r) => r.resolutionSeconds),
    [timelineRows],
  )
  const responseBuckets = useMemo(() => durationBuckets(responseSecondsRaw), [responseSecondsRaw])
  const resolutionBuckets = useMemo(() => durationBuckets(resolutionSecondsRaw), [resolutionSecondsRaw])
  const medianResponse = useMemo(() => medianOf(responseSecondsRaw), [responseSecondsRaw])
  const medianResolution = useMemo(() => medianOf(resolutionSecondsRaw), [resolutionSecondsRaw])
  const durationData = useMemo(
    () => ({
      labels: responseBuckets.map((b) => b.label),
      datasets: [
        barDataset('First response', responseBuckets.map((b) => b.value), VIZ.series[0]),
        barDataset(
          'Resolution',
          resolutionBuckets.map((b) => b.value),
          VIZ.series[2],
        ),
      ],
    }),
    [responseBuckets, resolutionBuckets],
  )

  // ── Needs attention ────────────────────────────────────────────────────────
  const attention = useMemo((): AttentionItem[] => {
    const items: AttentionItem[] = []
    for (const team of teams) {
      if (team.assigned === 0 && team.responses === 0) continue
      const response = team.responseSlaPct
      const resolution = team.resolutionSlaPct
      const reopenRate = ratePct(team.reOpened, team.assigned)

      if (response !== null && response < SLA_TARGET) {
        items.push({
          id: `${team.key}-response`,
          title: team.name,
          detail: `Response SLA over ${formatCount(team.assigned)} assigned`,
          value: formatPct(response, 1),
          tone: response < 75 ? 'critical' : 'warning',
        })
      } else if (resolution !== null && resolution < SLA_TARGET) {
        items.push({
          id: `${team.key}-resolution`,
          title: team.name,
          detail: `Resolution SLA over ${formatCount(team.closed)} closed`,
          value: formatPct(resolution, 1),
          tone: resolution < 75 ? 'critical' : 'warning',
        })
      } else if (reopenRate !== null && reopenRate > REOPEN_TARGET) {
        items.push({
          id: `${team.key}-reopen`,
          title: team.name,
          detail: `${formatCount(team.reOpened)} re-opened of ${formatCount(team.assigned)}`,
          value: formatPct(reopenRate, 1),
          tone: 'warning',
        })
      }
    }
    // Worst first — the panel is a work queue, not a directory.
    return items.sort((a, b) => parseFloat(a.value) - parseFloat(b.value)).slice(0, 8)
  }, [teams])

  if (teams.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            The current filters returned no team activity. Widen the window or clear the team filter,
            then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const percentAxis = barOptions({
    horizontal: true,
    legend: true,
    max: 100,
    format: (v) => `${Math.round(v)}%`,
  })
  const topTeam = byVolume[0]

  return (
    <AnalyticsCanvas>
      {/* ── The one number the view leads with ── */}
      <HeroBand
        eyebrow="Conversations assigned"
        value={formatCount(totals.assigned)}
        caption={`Handled by ${totals.activeTeams} team${totals.activeTeams === 1 ? '' : 's'} in the selected window`}
        spark={volumeShape}
        Icon={UsersRound}
        facts={[
          { label: 'Closed', value: formatCount(totals.closed) },
          { label: 'Closure rate', value: formatPct(totals.closureRate, 1) },
          {
            label: 'Response SLA',
            value: formatPct(totals.responseSla, 1),
            tone: slaTone(totals.responseSla),
          },
          {
            label: 'Resolution SLA',
            value: formatPct(totals.resolutionSla, 1),
            tone: slaTone(totals.resolutionSla),
          },
        ]}
      />

      {/* ── Supporting numbers ── */}
      <KpiRow>
        <StatTile
          label="In progress"
          value={formatCount(totals.workInProgress)}
          hint="Assigned but not yet closed"
        />
        <StatTile
          label="Re-opened"
          value={formatCount(totals.reOpened)}
          hint={`${formatPct(ratePct(totals.reOpened, totals.assigned), 1)} of assigned`}
          tone={reopenTone(ratePct(totals.reOpened, totals.assigned))}
        />
        <StatTile
          label="Avg first response"
          value={formatSeconds(totals.avgResponse)}
          hint={`${formatCount(totals.responses)} replies sent`}
        />
        <StatTile label="Avg resolution" value={formatSeconds(totals.avgResolution)} />
        <StatTile
          label="Notes added"
          value={formatCount(totals.notes)}
          hint={`${formatCount(totals.transfersOut)} transferred out`}
        />
        <StatTile
          label="Teams reporting"
          value={formatCount(totals.activeTeams)}
          {...(topTeam ? { hint: `Busiest: ${topTeam.name}` } : {})}
        />
      </KpiRow>

      {/* ── Leaderboard ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${teams.length} team${teams.length === 1 ? '' : 's'} in scope`}>
          Leaderboard
        </SectionLabel>
        <ChartCard
          title="Every team, ranked by volume"
          subtitle="Bar length shows each team's share of the busiest team's load"
          Icon={ListOrdered}
          badge="Scroll for more"
          height={340}
          wide
        >
          <RankTable
            rows={byVolume}
            keyOf={(t) => t.key}
            nameOf={(t) => t.name}
            shareOf={(t) => (maxAssigned > 0 ? t.assigned / maxAssigned : 0)}
            columns={[
              { label: 'Assigned', align: 'right', render: (t) => formatCount(t.assigned) },
              { label: 'Closed', align: 'right', render: (t) => formatCount(t.closed) },
              {
                label: 'Closure',
                align: 'right',
                render: (t) => formatPct(ratePct(t.closed, t.assigned), 1),
              },
              { label: 'Responses', align: 'right', render: (t) => formatCount(t.responses) },
              {
                label: 'Avg response',
                align: 'right',
                render: (t) => formatSeconds(t.avgResponseSeconds),
              },
              {
                label: 'Avg resolution',
                align: 'right',
                render: (t) => formatSeconds(t.avgResolutionSeconds),
              },
              {
                label: 'Response SLA',
                align: 'right',
                render: (t) => <SlaCell value={t.responseSlaPct} />,
              },
              {
                label: 'Resolution SLA',
                align: 'right',
                render: (t) => <SlaCell value={t.resolutionSlaPct} />,
              },
            ]}
          />
        </ChartCard>
      </section>

      {/* ── Volume ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Volume &amp; throughput</SectionLabel>
        <CardGrid>
          <ChartCard
            title="Assigned vs closed"
            subtitle={`Top ${Math.min(CHART_TEAMS, topForCharts.length)} teams by assigned volume`}
            Icon={TrendingUp}
            footnote={
              totals.closureRate !== null
                ? `Teams closed ${formatPct(totals.closureRate, 1)} of what was assigned in this window.`
                : undefined
            }
          >
            {topForCharts.length === 0 ? (
              <CardEmpty message="No conversations were assigned in this window." />
            ) : (
              <Chart
                type="bar"
                data={handledData}
                options={barOptions({ horizontal: true, legend: true })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Workload mix"
            subtitle="Closed, still in progress and re-opened, per team"
            Icon={Layers}
            footnote={`${formatCount(totals.workInProgress)} conversations are still open across teams.`}
          >
            {topForCharts.length === 0 ? (
              <CardEmpty message="No conversations were assigned in this window." />
            ) : (
              <Chart
                type="bar"
                data={mixData}
                options={barOptions({ horizontal: true, legend: true, stacked: true })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Service levels ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`Target ${SLA_TARGET}%`}>Service levels</SectionLabel>
        <CardGrid>
          <ChartCard
            title="SLA attainment by team"
            subtitle="Share of conversations inside target"
            Icon={Gauge}
          >
            {slaTeams.length === 0 ? (
              <CardEmpty message="No SLA-measurable conversations in this window." />
            ) : (
              <Chart type="bar" data={slaData} options={percentAxis} className="h-full w-full" />
            )}
          </ChartCard>

          <ChartCard
            title="Needs attention"
            subtitle={`Teams under the ${SLA_TARGET}% target, or re-opening more than ${REOPEN_TARGET}%`}
            Icon={AlertTriangle}
          >
            <AttentionList
              items={attention}
              emptyMessage={`Every reporting team is at or above the ${SLA_TARGET}% target.`}
            />
          </ChartCard>

          <ChartCard
            title="Load vs attainment"
            subtitle="Does service level hold as volume rises?"
            Icon={Activity}
            footnote="Each dot is one team. A cloud sloping down to the right means attainment is falling as load grows."
          >
            {scatterTeams.length < 2 ? (
              <CardEmpty message="At least two teams with SLA-measurable volume are needed to compare." />
            ) : (
              <Chart
                type="scatter"
                data={scatterData}
                options={scatterOptions({
                  xLabel: 'Assigned',
                  yLabel: 'Response SLA',
                  yFormat: (v) => `${Math.round(v)}%`,
                  yMax: 100,
                  pointLabel: (index) => scatterTeams[index]?.name ?? 'Team',
                })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Handling time by team"
            subtitle="Average first response and resolution"
            Icon={Timer}
          >
            {timeTeams.length === 0 ? (
              <CardEmpty message="Nothing was responded to or resolved in this window." />
            ) : (
              <Chart
                type="bar"
                data={timeData}
                options={barOptions({ horizontal: true, legend: true, format: formatSeconds })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Outcome mix ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Outcomes</SectionLabel>
        <CardGrid>
          <ChartCard
            title="Where conversations stand"
            subtitle="Every assigned conversation, by outcome"
            height={220}
          >
            <Meter
              segments={[
                { label: 'Closed', value: totals.closed, color: VIZ.series[2] },
                { label: 'In progress', value: totals.workInProgress, color: VIZ.series[0] },
                { label: 'Re-opened', value: totals.reOpened, color: VIZ.series[1] },
              ]}
              emptyMessage="No conversations were assigned in this window."
            />
          </ChartCard>
          <ChartCard
            title="Transfers"
            subtitle="Conversations moved into vs. out of teams"
            Icon={ArrowLeftRight}
            height={220}
          >
            <Meter
              segments={[
                { label: 'Transferred in', value: totals.transfersIn, color: VIZ.series[0] },
                { label: 'Transferred out', value: totals.transfersOut, color: VIZ.series[1] },
              ]}
              emptyMessage="No transfers in this window."
            />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Load concentration ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${formatCount(totals.assigned)} assigned across ${teams.length} team${teams.length === 1 ? '' : 's'}`}>
          Load concentration
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Share of assigned volume"
            subtitle="Each team's slice of everything assigned in this window"
            Icon={UsersRound}
            height={320}
          >
            <BarList
              items={shareItems}
              format={formatCount}
              emptyMessage="No conversations were assigned in this window."
            />
          </ChartCard>

          <ChartCard
            title="Transferred in vs. out, by team"
            subtitle="Where handoffs are landing and leaving from"
            Icon={ArrowLeftRight}
          >
            {transferTeams.length === 0 ? (
              <CardEmpty message="No transfers in this window." />
            ) : (
              <Chart
                type="bar"
                data={transferData}
                options={barOptions({ horizontal: true, legend: true })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Collaboration ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Collaboration by team</SectionLabel>
        <ChartCard
          title="Responses sent vs. notes added"
          subtitle="Customer-facing replies alongside internal notes, per team"
          Icon={Layers}
          height={320}
          wide
        >
          {collabTeams.length === 0 ? (
            <CardEmpty message="No responses or notes recorded in this window." />
          ) : (
            <Chart
              type="bar"
              data={collabData}
              options={barOptions({ horizontal: true, legend: true, stacked: false })}
              className="h-full w-full"
            />
          )}
        </ChartCard>
      </section>

      {/* ── Timing ── */}
      {/* Sourced from timelineRows (per-conversation), not the aggregate rows —
          see file header. Loads separately, so it never blocks the rest of the
          view; a quiet spinner and an empty state cover the in-between. */}
      <section className="flex flex-col gap-3">
        <SectionLabel
          {...(timelineTruncated
            ? { note: 'Capped at the first 20,000 conversations in this window' }
            : {})}
        >
          Timing
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="When conversations arrive"
            subtitle="Volume by day of week and hour"
            Icon={CalendarClock}
            wide
          >
            {timelineLoading ? (
              <div className="flex h-full items-center justify-center gap-2 text-[12px] text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading timing data…
              </div>
            ) : openedMatrix.total === 0 ? (
              <CardEmpty message="No conversation timestamps in this window." />
            ) : (
              <Heatmap matrix={openedMatrix.matrix} max={openedMatrix.max} />
            )}
          </ChartCard>

          <ChartCard
            title="Response and resolution time"
            subtitle="How long conversations take, banded"
            Icon={Timer}
            footnote={
              medianResponse !== null || medianResolution !== null
                ? `Median first response ${formatSeconds(medianResponse)} · median resolution ${formatSeconds(medianResolution)}.`
                : undefined
            }
          >
            {timelineLoading ? (
              <div className="flex h-full items-center justify-center gap-2 text-[12px] text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading timing data…
              </div>
            ) : timelineRows.length === 0 ? (
              <CardEmpty message="No conversation timestamps in this window." />
            ) : (
              <Chart
                type="bar"
                data={durationData}
                options={barOptions({ horizontal: false, legend: true, format: formatSeconds })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Fastest first responses"
            subtitle="Share of replies inside each time band"
            Icon={Clock}
          >
            {timelineLoading ? (
              <div className="flex h-full items-center justify-center gap-2 text-[12px] text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading timing data…
              </div>
            ) : responseSecondsRaw.every((v) => v === null) ? (
              <CardEmpty message="Nothing has been responded to yet in this window." />
            ) : (
              <BarList
                items={responseBuckets.map((b) => ({ label: b.label, value: b.value }))}
                format={formatCount}
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}

/** SLA cell: the number leads, a dot carries the state so it isn't color-only. */
function SlaCell({ value }: { value: number | null }): JSX.Element {
  const tone = slaTone(value)
  const dot =
    tone === 'good' ? 'bg-[#0CA30C]' : tone === 'warning' ? 'bg-[#FAB219]' : tone === 'critical' ? 'bg-[#D03B3B]' : 'bg-slate-300'
  return (
    <span className="inline-flex items-center justify-end gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden="true" />
      {formatPct(value, 1)}
    </span>
  )
}

/**
 * SLA tone. Thresholds are the conventional 95/85 service-level bands, and the
 * color never travels alone — the label and value always say what it is.
 */
function slaTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct >= 95) return 'good'
  if (pct >= 85) return 'warning'
  return 'critical'
}

function reopenTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct <= 5) return 'good'
  if (pct <= REOPEN_TARGET) return 'warning'
  return 'critical'
}
